import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';

@Directive({
    standalone: true,
    selector: '[appColResize]',
})
export class ColResizeDirective implements AfterViewInit, OnDestroy {
    @Input() appColResizeSkip: string[] = ['checkbox'];
    @Input() appColResizeMinWidth: number = 80;

    @Input() set appColResizeTrigger(_value: unknown) {
        if (this.isInitialized) {
            setTimeout(() => this.applyColumnWidths());
        }
    }

    private isInitialized = false;
    private readonly colWidths = new Map<string, number>();
    private readonly resizeCleanups: Array<() => void> = [];
    private observer: MutationObserver | null = null;
    private reinitTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private readonly el: ElementRef) {}

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.initColumnResize();
            this.isInitialized = true;
            this.observeHeaderChanges();
        }, 200);
    }

    private observeHeaderChanges(): void {
        const host = this.el.nativeElement as HTMLElement;
        const target = host.querySelector<HTMLElement>('table[mat-table]') ?? host;

        this.observer = new MutationObserver((mutations) => {
            const hasHeaderCellChange = mutations.some(
                (m) =>
                    Array.from(m.addedNodes).some((n) => n instanceof HTMLElement && n.matches('th.mat-mdc-header-cell')) ||
                    Array.from(m.removedNodes).some((n) => n instanceof HTMLElement && n.matches('th.mat-mdc-header-cell'))
            );

            if (!hasHeaderCellChange) return;

            if (this.reinitTimer !== null) {
                clearTimeout(this.reinitTimer);
            }
            this.reinitTimer = setTimeout(() => {
                this.reinitTimer = null;
                this.reinitializeColumnResize();
                this.applyColumnWidths();
            }, 50);
        });

        this.observer.observe(target, { childList: true, subtree: true });
    }

    private reinitializeColumnResize(): void {
        const host = this.el.nativeElement as HTMLElement;
        host.querySelectorAll<HTMLElement>('.col-resize-handle').forEach((handle) => handle.remove());
        this.resizeCleanups.forEach((cleanup) => cleanup());
        this.resizeCleanups.length = 0;
        this.initColumnResize();
    }

    private initColumnResize(): void {
        const host = this.el.nativeElement as HTMLElement;
        host.querySelectorAll<HTMLElement>('th.mat-mdc-header-cell').forEach((th) => {
            const colKey = this.getColKey(th);
            if (!colKey || this.appColResizeSkip.includes(colKey)) return;

            const handle = document.createElement('div');
            handle.className = 'col-resize-handle';
            th.appendChild(handle);

            const onMouseDown = (e: MouseEvent): void => this.onResizeStart(e, th);
            handle.addEventListener('mousedown', onMouseDown);
            this.resizeCleanups.push(() => handle.removeEventListener('mousedown', onMouseDown));
        });
    }

    private onResizeStart(e: MouseEvent, th: HTMLElement): void {
        e.stopPropagation();
        e.preventDefault();

        const startX = e.clientX;
        const startWidth = th.offsetWidth;
        const colKey = this.getColKey(th);

        const onMouseMove = (ev: MouseEvent): void => {
            const newWidth = Math.max(startWidth + ev.clientX - startX, this.appColResizeMinWidth);
            const widthPx = `${newWidth}px`;
            this.setWidth(th, widthPx);
            if (colKey) {
                this.colWidths.set(colKey, newWidth);
                th.closest('table')?.querySelectorAll<HTMLElement>(`td.mat-column-${colKey}`).forEach((td) => {
                    td.classList.add('col-resize-applied');
                    this.setWidth(td, widthPx);
                });
            }
        };

        const onMouseUp = (): void => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }

    private applyColumnWidths(): void {
        if (this.colWidths.size === 0) return;
        const host = this.el.nativeElement as HTMLElement;
        this.colWidths.forEach((width, colKey) => {
            const widthPx = `${width}px`;
            host.querySelectorAll<HTMLElement>(`th.mat-column-${colKey}, td.mat-column-${colKey}`).forEach((cell) => {
                this.setWidth(cell, widthPx);
            });
        });
    }

    private setWidth(el: HTMLElement, widthPx: string): void {
        el.style.setProperty('width', widthPx, 'important');
        el.style.setProperty('min-width', widthPx, 'important');
        el.style.setProperty('max-width', widthPx, 'important');
    }

    private getColKey(th: HTMLElement): string | null {
        return (
            Array.from(th.classList)
                .find((c) => c.startsWith('mat-column-'))
                ?.replace('mat-column-', '') ?? null
        );
    }

    ngOnDestroy(): void {
        this.resizeCleanups.forEach((cleanup) => cleanup());
        this.observer?.disconnect();
        if (this.reinitTimer !== null) {
            clearTimeout(this.reinitTimer);
        }
    }
}
